import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Select } from '../../components/controls/Select.jsx';
import {
  isOrganizationId,
  fillLabelFromPlatform,
  normalizeId,
  SEGMENT_LABEL,
  SEGMENT_OPTIONS,
  toPlatformOrgItems,
} from '../../lib/platformOrgs.js';

function PlatformName({ lookup }) {
  if (!lookup) return null;
  if (lookup.status === 'loading')
    return <small className="platform-orgs-lookup">Looking up…</small>;
  if (lookup.status === 'error' || lookup.configured === false) return null;
  if (!lookup.known)
    return (
      <small className="platform-orgs-lookup is-warn">
        Not found on the platform
      </small>
    );
  return (
    <small className="platform-orgs-lookup">
      Platform: <b>{lookup.name || '—'}</b>
      {lookup.deleted ? ' · deleted' : ''}
    </small>
  );
}

const blankRow = () => ({ organizationId: '', label: '', segmentBy: 'none' });

export function PlatformOrgsGroup({
  clientId,
  orgs,
  isAdmin,
  canEdit,
  onEditingChange,
  reloadDetail,
}) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const editing = rows !== null;

  function start() {
    setRows(orgs.length ? orgs.map((o) => ({ ...o })) : [blankRow()]);
    setError('');
    onEditingChange(true);
  }
  function cancel() {
    setRows(null);
    setError('');
    onEditingChange(false);
  }
  const [lookups, setLookups] = useState({});
  async function lookUp(i) {
    const id = normalizeId(rows[i]?.organizationId);
    if (!isOrganizationId(id) || lookups[id]) return;
    setLookups((current) => ({ ...current, [id]: { status: 'loading' } }));
    try {
      const found = await api(`/usage/platform-orgs/${id}`);
      setLookups((current) => ({ ...current, [id]: found }));
      if (found.known && found.name)
        setRows((current) => fillLabelFromPlatform(current, id, found.name));
    } catch {
      setLookups((current) => ({ ...current, [id]: { status: 'error' } }));
    }
  }

  const update = (i, patch) =>
    setRows((current) =>
      current.map((row, j) => (j === i ? { ...row, ...patch } : row)),
    );

  async function save() {
    const { items, error: invalid } = toPlatformOrgItems(rows);
    if (invalid) return setError(invalid);
    setSaving(true);
    setError('');
    try {
      await api(`/clients/${clientId}/platform-orgs`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      await reloadDetail();
      setRows(null);
      onEditingChange(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={`info-group editable-group platform-orgs${editing ? ' is-editing' : ''}`}
    >
      <div className="info-group-head">
        <h3>Platform organizations</h3>
        {isAdmin && canEdit && !editing && (
          <button
            type="button"
            className="group-edit"
            onClick={start}
            aria-label="Edit Platform organizations"
          >
            <Pencil size={14} />
          </button>
        )}
        {editing && <span className="platform-orgs-tag">Admin only</span>}
      </div>

      {!editing && orgs.length === 0 && (
        <p className="muted-text platform-orgs-empty">
          No platform organization linked
          {isAdmin ? ' — use the pencil to link one.' : '.'}
        </p>
      )}

      {(editing || orgs.length > 0) && (
        <div className="platform-orgs-grid" role="table">
          <div className="platform-orgs-row platform-orgs-hdr" role="row">
            <span role="columnheader">Label</span>
            <span role="columnheader">Organization id</span>
            <span role="columnheader">Voice calls breakdown</span>
            <span aria-hidden="true" />
          </div>
          {!editing &&
            orgs.map((o) => (
              <div
                className="platform-orgs-row"
                role="row"
                key={o.organizationId}
              >
                <b role="cell">{o.label || '—'}</b>
                <code role="cell">{o.organizationId}</code>
                <span role="cell">
                  <span className="platform-orgs-seg">
                    {SEGMENT_LABEL[o.segmentBy]}
                  </span>
                </span>
                <span aria-hidden="true" />
              </div>
            ))}
          {editing &&
            rows.map((r, i) => (
              <div className="platform-orgs-row" role="row" key={i}>
                <input
                  aria-label="Label"
                  placeholder="e.g. Volsor 2"
                  value={r.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  disabled={saving}
                />
                <div className="platform-orgs-id">
                  <input
                    aria-label="Organization id"
                    placeholder="24-character platform organization id"
                    value={r.organizationId}
                    onChange={(e) =>
                      update(i, { organizationId: e.target.value })
                    }
                    onBlur={() => lookUp(i)}
                    disabled={saving}
                  />
                  <PlatformName
                    lookup={lookups[normalizeId(r.organizationId)]}
                  />
                </div>
                <Select
                  ariaLabel="Voice calls breakdown"
                  value={r.segmentBy}
                  onChange={(segmentBy) => update(i, { segmentBy })}
                  disabled={saving}
                  options={SEGMENT_OPTIONS.map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Remove ${r.label || r.organizationId || 'row'}`}
                  onClick={() =>
                    setRows((current) => current.filter((_, j) => j !== i))
                  }
                  disabled={saving}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
        </div>
      )}

      {!editing && orgs.length > 0 && (
        <p className="muted-text platform-orgs-note">
          Usage of these organizations counts as this client’s in Transaction
          data. Changes apply from the next recompute.
        </p>
      )}

      {editing && (
        <div className="editable-group-foot">
          {error && <div className="error">{error}</div>}
          <div className="platform-orgs-actions">
            <button
              type="button"
              className="secondary small"
              onClick={() => setRows((current) => [...current, blankRow()])}
              disabled={saving}
            >
              <Plus size={14} /> Add organization
            </button>
            <div className="editable-group-actions">
              <button
                type="button"
                className="text-button"
                onClick={cancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary small"
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
